import { ReactNode } from "react"
import { Decimal } from "shared-ts/classes/decimal"
import { Money } from "shared-ts/classes/money"
import { SelectMenu } from "shared-web/components/base/input/SelectMenu"
import { TextDecimalInput } from "shared-web/components/base/input/text/TextDecimalInput"
import { TextMoneyInput } from "shared-web/components/base/input/text/TextMoneyInput"

import { onPressEnterSubmitHandler } from "components/composite/TenderDrawer/views/tenderDrawerViews.utils"

export const ValueTypes = {
  PERCENTAGE: "%",
  DOLLARS: "$",
} as const

export type ValueType = (typeof ValueTypes)[keyof typeof ValueTypes]

interface ValueTypeInputProps {
  valueType: ValueType
  percentageValue?: Decimal
  percentageValueNumDecimalPlaces?: number
  moneyValue?: Money
  onValueTypeChange: (valueType: ValueType) => void
  onPercentageChange: (value: Decimal) => void
  onMoneyChange: (value: Money) => void
  onBlur: () => void
  disabled?: boolean
  errorMessage?: string
}

export function ValueTypeInput({
  valueType,
  percentageValue,
  percentageValueNumDecimalPlaces = 3,
  moneyValue,
  onPercentageChange,
  onMoneyChange,
  onValueTypeChange,
  onBlur,
  disabled,
  errorMessage,
}: ValueTypeInputProps) {
  const invalid = Boolean(errorMessage)

  const inputByValueType: Record<ValueType, ReactNode> = {
    [ValueTypes.DOLLARS]: (
      <TextMoneyInput
        className="pr-30"
        money={moneyValue ?? Money.zero()}
        onChangeMoney={onMoneyChange}
        onBlurMoney={() => onBlur()}
        leftText={null}
        invalid={invalid}
        errorMessage={errorMessage}
        disabled={disabled}
        onKeyDown={onPressEnterSubmitHandler(onBlur, false)}
        containerClassName="flex-1"
      />
    ),
    [ValueTypes.PERCENTAGE]: (
      <TextDecimalInput
        onChangeDecimal={onPercentageChange}
        decimal={percentageValue ?? Decimal.zero()}
        numDecimalPlaces={percentageValueNumDecimalPlaces}
        trimTrailingZeros={true}
        disabled={disabled}
        invalid={invalid}
        onBlur={onBlur}
        onKeyDown={onPressEnterSubmitHandler(onBlur, false)}
        errorMessage={errorMessage}
        containerClassName="flex-1"
      />
    ),
  }

  return (
    <div>
      <div className="mt-1 flex items-center gap-x-2 sm:col-span-6">
        {inputByValueType[valueType]}
        <SelectMenu<ValueType>
          items={[ValueTypes.DOLLARS, ValueTypes.PERCENTAGE]}
          selectedItem={valueType}
          onSelectItem={onValueTypeChange}
          keyExtractor={(item) => item}
          valueExtractor={(item) => item}
          disabled={disabled}
          invalid={invalid}
        />
      </div>
    </div>
  )
}
